import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectService } from '../services/api';
import { 
  Briefcase, 
  DollarSign, 
  Calendar, 
  MapPin, 
  FileText, 
  Tag,
  Send,
  AlertCircle
} from 'lucide-react';

const PostProject: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    skills: '',
    budget: {
      type: 'hourly', // 'hourly' or 'fixed'
      min: '',
      max: '',
    },
    timeline: '',
    location: 'remote', // 'remote', 'onsite', 'hybrid'
    experience_level: 'intermediate', // 'entry', 'intermediate', 'expert'
    additional_details: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('budget.')) {
      const budgetField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        budget: {
          ...prev.budget,
          [budgetField]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.title.trim() || !formData.description.trim() || !formData.category) {
        throw new Error('Please fill in all required fields');
      }

      if (!formData.budget.min || !formData.budget.max) {
        throw new Error('Please specify both minimum and maximum budget');
      }

      if (parseFloat(formData.budget.min) > parseFloat(formData.budget.max)) {
        throw new Error('Minimum budget cannot be greater than maximum budget');
      }

      // Prepare project data for API
      const projectData = {
        ...formData,
        client_id: user?.id,
        client_name: user?.name || '',
        client_email: user?.email || '',
      };

      // Save project to database
      const savedProject = await projectService.create(projectData);
      
      console.log('Project saved successfully:', savedProject);
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/client-projects');
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to post project. Please try again.';
      setError(errorMessage);
      console.error('Error posting project:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    'Web Development',
    'Mobile Development',
    'Design & Creative',
    'Writing & Translation',
    'Digital Marketing',
    'Data Science & Analytics',
    'Engineering & Architecture',
    'Admin & Customer Support',
    'Legal',
    'Other'
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level', description: '0-2 years experience' },
    { value: 'intermediate', label: 'Intermediate', description: '2-5 years experience' },
    { value: 'expert', label: 'Expert', description: '5+ years experience' },
  ];

  if (!user || user.userType !== 'client') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={40} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
          <p className="text-white/70 mb-4">This page is only accessible to clients.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-white/70">Loading...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-500/30">
            <Send size={20} className="text-green-400 sm:w-6 sm:h-6" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-2">Project Posted Successfully!</h2>
          <p className="text-sm sm:text-base text-white/70">Your project is now live and visible to freelancers.</p>
          <p className="text-xs sm:text-sm text-white/50 mt-2">Redirecting to your projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-white/10 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Post a New Project
            </h1>
            <p className="text-base sm:text-lg text-white/70">
              Describe your project and find the perfect freelancer
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-md p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle size={16} className="text-red-400" />
                <p className="text-red-300 text-sm sm:text-base">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
            {/* Project Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-white mb-2">
                Project Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                placeholder="e.g., Build a React e-commerce website"
                value={formData.title}
                onChange={handleInputChange}
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-white mb-2">
                Category *
              </label>
              <select
                id="category"
                name="category"
                required
                className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Project Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-white mb-2">
                Project Description *
              </label>
              <textarea
                id="description"
                name="description"
                rows={6}
                required
                className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                placeholder="Describe your project requirements, goals, and any specific details..."
                value={formData.description}
                onChange={handleInputChange}
              />
            </div>

            {/* Required Skills */}
            <div>
              <label htmlFor="skills" className="block text-sm font-medium text-white mb-2">
                Required Skills
              </label>
              <input
                type="text"
                id="skills"
                name="skills"
                className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                placeholder="e.g., React, Node.js, MongoDB, UI/UX Design"
                value={formData.skills}
                onChange={handleInputChange}
              />
              <p className="text-xs sm:text-sm text-white/50 mt-1">Separate skills with commas</p>
            </div>

            {/* Budget */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Budget *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <select
                    name="budget.type"
                    className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white"
                    value={formData.budget.type}
                    onChange={handleInputChange}
                  >
                    <option value="hourly">Hourly Rate</option>
                    <option value="fixed">Fixed Price</option>
                  </select>
                </div>
                <div>
                  <input
                    type="number"
                    name="budget.min"
                    placeholder="Min"
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                    value={formData.budget.min}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <input
                    type="number"
                    name="budget.max"
                    placeholder="Max"
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                    value={formData.budget.max}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <p className="text-xs sm:text-sm text-white/50 mt-1">
                {formData.budget.type === 'hourly' ? 'Hourly rate in USD' : 'Fixed price in USD'}
              </p>
            </div>

            {/* Timeline */}
            <div>
              <label htmlFor="timeline" className="block text-sm font-medium text-white mb-2">
                Timeline
              </label>
              <select
                id="timeline"
                name="timeline"
                className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white"
                value={formData.timeline}
                onChange={handleInputChange}
              >
                <option value="">Select timeline</option>
                <option value="less_than_1_week">Less than 1 week</option>
                <option value="1_to_2_weeks">1 to 2 weeks</option>
                <option value="2_to_4_weeks">2 to 4 weeks</option>
                <option value="1_to_3_months">1 to 3 months</option>
                <option value="3_to_6_months">3 to 6 months</option>
                <option value="more_than_6_months">More than 6 months</option>
              </select>
            </div>

            {/* Location */}
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-white mb-2">
                Work Location
              </label>
              <select
                id="location"
                name="location"
                className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white"
                value={formData.location}
                onChange={handleInputChange}
              >
                <option value="remote">Remote</option>
                <option value="onsite">On-site</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            {/* Experience Level */}
            <div>
              <label htmlFor="experience_level" className="block text-sm font-medium text-white mb-2">
                Experience Level
              </label>
              <div className="space-y-3">
                {experienceLevels.map(level => (
                  <label key={level.value} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="experience_level"
                      value={level.value}
                      checked={formData.experience_level === level.value}
                      onChange={handleInputChange}
                      className="text-primary-600 focus:ring-primary-500 mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-white text-sm sm:text-base">{level.label}</div>
                      <div className="text-xs sm:text-sm text-white/50">{level.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Details */}
            <div>
              <label htmlFor="additional_details" className="block text-sm font-medium text-white mb-2">
                Additional Details
              </label>
              <textarea
                id="additional_details"
                name="additional_details"
                rows={4}
                className="w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                placeholder="Any additional information, requirements, or preferences..."
                value={formData.additional_details}
                onChange={handleInputChange}
              />
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full sm:w-auto px-6 py-2 border border-white/20 rounded-md text-white/70 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Posting...</span>
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    <span>Post Project</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PostProject; 